import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Value } from "baseui/select";
import { colors, glyphs, useMediaQuery } from "../../../Styles";
import { SelectField } from "../..";
import { field, FilterContent, FilterDisplayType, FilterDisplayEnum, FieldTypes } from "../../../Definitions";

import { AnimalGroupData } from "../../../Assets/TestData/animalGroups";
import { ConditionData } from "../../../Assets/TestData/conditions";
import { LocationData } from "../../../Assets/TestData/locations";
import { TestData as FeedPlanData } from "../../../Assets/TestData/feedCurvesData";

// #region | Data: Action Parameters
export const actionParameters: FilterDisplayType[] = [
  { label: "Location", id: 1, displayType: FilterDisplayEnum.select, options: LocationData.map(location => ({
    label: location.locationName,
    id: location.locationId
  }))},
  // { label: "Attention", id: 2, displayType: FilterDisplayEnum.select }, // Later
  { label: "Group", id: 3, displayType: FilterDisplayEnum.select, options: AnimalGroupData.map(group => ({
    label: group.groupName,
    id: group.groupId
  }))},
  { label: "Condition", id: 4, displayType: FilterDisplayEnum.select, options: ConditionData.map(condition => ({
    label: condition.conditionName,
    id: condition.conditionId
  }))},
  { label: "Remove Animals", id: 5 },
  { label: "Feed Plan", id: 6, displayType: FilterDisplayEnum.select, options: FeedPlanData.map(feedPlan => ({
    label: feedPlan.name,
    id: feedPlan.id
  }))},
  { label: "Reset Total Consumed", id: 7 },
  { label: "Manual Feed Start", id: 8 },
  // { label: "Marking", id: 9, displayType: FilterDisplayEnum.select }, // Later
  { label: "Clear Calendar Events", id: 10 },
  // { label: "In Heat", id: 11, displayType: FilterDisplayEnum.select }, // Later
  { label: "Insemination", id: 12, displayType: FilterDisplayEnum.date }, // From milestones
  { label: "Weaning", id: 13, displayType: FilterDisplayEnum.date }, // From milestones
  { label: "Farrowing", id: 14, displayType: FilterDisplayEnum.date }, // From milestones
  { label: "Pregnancy Check", id: 15, displayType: FilterDisplayEnum.select, options: [{label: "Pregnant", id: 1}, {label: "Not Pregnant", id: 2}] },
  { label: "Birth Date", id: 16, displayType: FilterDisplayEnum.date },
  // { label: "Memo", id: 17 } // Later
]
// #endregion

export default function QuickEntryModalLvl1() {
  const {t} = useTranslation()
  const mobileXs = useMediaQuery(`(max-width: 690px)`);
  const [selectFields, setSelectFields] = useState<field[]>([{ id: -1 }]);

  // SelectField Options
  const filterParamOptions = actionParameters.map((type) => ({
    label: t(`report.filterParameter.${type.label}`, {defaultValue: type.label}), 
    id: type.id
  }));

  // Handle onChange on SelectField
  const handleFilterTypeChange = (index: number, value: Value) => {
    const updatedSelectFields = [...selectFields];
    if (value.length > 0) {

      if (updatedSelectFields[index].id === -1) {
        updatedSelectFields.push({id: -1})
      }
      updatedSelectFields[index] = {id: value[0].id ? +value[0].id : 0, value: undefined};
    } else {
      updatedSelectFields[index].id = undefined;
    }
    setSelectFields(updatedSelectFields);
  };

  const handleFilterValueChange = (index?: number, value?: Value) => {
    const updatedSelectFields = [...selectFields];
    if (index && value && value.length > 0) {
      updatedSelectFields[index].value = value[0].id ? +value[0].id : 0;
    }
    setSelectFields(updatedSelectFields);
  }

  // Function to remove a SelectField by index
  const handleRemoveField = useCallback((index: number) => {
    const oldSelectedFields = [...selectFields];
    oldSelectedFields.splice(index, 1)
    setSelectFields(oldSelectedFields)
  },[selectFields]);

  const renderedFields = selectFields.map((field, index) => {return (
    <div 
      key={index} 
      style={{ 
        width: "100%", 
        display: "flex", 
        flexDirection: "column", 
        gap: "10px", 
        marginBottom: index === selectFields.length - 1 ? "0px" : "20px"
      }}
    >
      <div style={{ display: "flex", flexDirection: "row", gap: "20px", alignItems: "center", justifyContent: "space-between" }}>
        <SelectField
          
          placeholder={t("select")+"..."}
          key={index+String(field.id)}
          options={filterParamOptions}
          onChange={(value) => handleFilterTypeChange(index, value)}
          defaultValue={filterParamOptions.find((parameter) => {
            return parameter.id === field.id
          })?.label}
          overrides={{
            fieldType: FieldTypes.primaryInputField,
            textStart: true,
            fieldWidth: mobileXs ? "180px" : "280px",
            dropdownWidth: "280px",
            dropdownBorderRadius: mobileXs ? "0px 10px 10px 10px" : "0px 0px 10px 10px"
          }}
        />
        {field.id !== -1 && (
          <>
            <div style={{cursor: "pointer"}} onClick={() => handleRemoveField(index)}>
              <glyphs.Delete size="25px"/>
            </div>
          </>
        )}
      </div>
      {/* Content based on selected filter parameter */}
      {field.id !== -1 && (
        <FilterContent filter={field} index={index} filterParameterTypes={actionParameters} handleFilterValueChange={handleFilterValueChange} />
      )}
    </div>
  )})
  // #endregion
  
  return(
    <div style={{ width: mobileXs ? "290px" : "600px" }}>
      <div style={{
        borderTop: "2px solid " + colors.blueDarkSecondary,
        borderBottom: "2px solid " + colors.blueDarkSecondary,
        padding: mobileXs? "10px 0px" : "10px",
        maxHeight: mobileXs ? "40vh" : "50vh",
        overflowY: "scroll",
        overflowX: "hidden",
        scrollbarWidth: "thin",
        scrollbarColor: colors.blueDarkSecondary+colors.transparent,
        display: "flex",
        alignItems: "center",
        justifyContent: "start",
        flexDirection: "column",
        gap: "10px"
      }}>
        {renderedFields}
      </div>
    </div>
  )
}